<template>
	<v-dialog v-model="dialog" max-width="600px">
		<v-card v-if="status">
			<v-card-title> {{ device }} {{ alarm }} </v-card-title>
			<v-card-subtitle class="pb-0">
				Changing status to {{ status.text }}
			</v-card-subtitle>
			<v-card-text class="pb-0">
				<v-alert v-if="error" type="error" dismissible>{{ error }}</v-alert>
				<v-form ref="form" v-model="valid" lazy-validation>
					<v-row>
						<v-col v-if="status.value.includes('DISABLED')">
							<v-select v-model="form.reason" label="Reason" :items="reasons" />
						</v-col>
						</v-row>
						<v-row>
						<v-col v-if="status.value === 'SNOOZE_DISABLED'">
							<v-select
								v-model="form.snooze"
								label="Snooze time"
								:items="snoozeOptions"
								:rules="[required, isFuture]"
								@input="triggerPopup"
							/>
						</v-col>
					</v-row>
					<v-textarea
						v-if="!status.value.includes('DISABLED') || form.reason === 'Other'"
						v-model="form.comments"
						:disabled="loading"
						label="Comment"
						:rules="[required, maxLength]"
						single-line
						counter="250"
					></v-textarea>
				</v-form>
			</v-card-text>
			<v-card-actions class="justify-end">
				<v-btn :disabled="loading" text @click="close">Cancel</v-btn>
				<v-btn :loading="loading" outlined color="ocean" :disabled="!valid" @click="submit"
					>Submit</v-btn
				>
			</v-card-actions>
		</v-card>
		<v-dialog v-model="timeDateDialog" max-width="400px" persistent>
			<v-card>
				<v-card-title>Select date and time</v-card-title>
				<v-card-text>
					<v-row>
						<v-col>
							<v-text-field
								v-model="form.snoozeDate"
								label="Date"
								readonly
								clearable
								hide-details
								@click.native="active = 'date'"
							/>
						</v-col>
						<v-col>
							<v-text-field
								v-model="form.snoozeTime"
								label="Time"
								readonly
								clearable
								hide-details
								@click.native="active = 'time'"
							/>
						</v-col>
					</v-row>
					<v-date-picker
						v-if="active === 'date'"
						v-model="form.snoozeDate"
						:min="new Date().toISOString()"
						full-width
					/>
					<v-time-picker
						v-else-if="active === 'time'"
						v-model="form.snoozeTime"
						:min="minTime"
						full-width
					/>
				</v-card-text>
				<v-card-actions class="justify-end">
					<v-btn outlined color="ocean" @click="setSnoozeDateTime">OK</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
import _ from "lodash";
import { alarmTypes } from "../status";
import { formatShortDateTime } from "@/utils/dateHelpers";

const form = () => ({
	reason: null,
	comments: "",
	snooze: 3600000,
	snoozeDate: null,
	snoozeTime: null,
});

export default {
	data() {
		return {
			dialog: false,
			status: null,
			ticket: null,
			form: form(),
			valid: false,
			active: "date",
			loading: false,
			error: null,
			timeDateDialog: false,
			disableReasons: ["Permanently Removed / Uninstalled", "Missing, Not Replacing", "Other"],
			snoozeReasons: ["Broken, Ordering Replacement", "Missing, Ordering Replacement", "Remodeling", "Other"],
			required: (val) => !!val || 'Required!',
			maxLength: (val) => (val && val.length <= 250) || 'Max length reached!',
			isFuture: (val) => val.toString().length < 13 || new Date().toISOString() < new Date(val).toISOString() || "Can't snooze to a past date!"
		};
	},

	computed: {
		alarm() {
			return _.get(
				_.find(alarmTypes, { value: _.get(this.ticket, "alarm_type") }),
				"text"
			);
		},
		device() {
			return _.get(this.ticket, "path", "")
				.replace("Devices/", "")
				.replaceAll("/", " ");
		},
		user() {
			const fullName = this.$store.getters["auth/fullName"];
			const email = this.$store.getters["auth/email"];
			return fullName === " " ? email : fullName;
		},
		minTime() {
			const date = new Date();
			if (new Date(this.form.snoozeDate + "T00:00").toDateString() === date.toDateString())
				return `${date.getHours()}:${date.getMinutes()}`;
			return null;
		},
		snoozeOptions() {
			const options = [
				{ text: "1 hour", value: 3600000 },
				{ text: "24 hours", value: 86400000 },
				{ text: "72 hours", value: 259200000 },
			];
			if (this.form.snoozeDate) {
				const date = `${this.form.snoozeDate}T${
					this.form.snoozeTime || "00:00"
				}`;
				const option = {
					text: formatShortDateTime(date),
					value: date,
				};
				options.push(option);
			}
			options.push({ text: "Select date/time...", value: "popup" });
			return options;
		},

		reasons() {
			return this.status.value === "DISABLED" ? this.disableReasons : this.snoozeReasons;
		}
	},

	watch: {
		dialog(open) {
			if (!open) {
				this.$refs.form.resetValidation();
			}
		}
	},

	methods: {
		pop({ status, ticket }) {
			this.status = status;
			this.ticket = ticket;
			this.dialog = true;
		},

		close() {
			Object.assign(this.form, form());
			this.dialog = false;
		},

		triggerPopup(snoozeTime) {
			if (snoozeTime === "popup") {
				this.timeDateDialog = true;
			}
		},

		setSnoozeDateTime() {
			this.timeDateDialog = false;
			this.form.snooze = this.form.snoozeDate && `${this.form.snoozeDate}T${
				this.form.snoozeTime || "00:00"
			}`;
		},

		async submit() {
			if (this.loading) return;
			if (!this.$refs.form.validate()) return;
			this.loading = true;
			const payload = {
				id: this.ticket.id,
				alarmStatus: this.status.value,
				actionType: this.status.action,
				comments: this.form.comments || this.form.reason,
				updatedBy: this.user,
			};

			if (payload.alarmStatus === "SNOOZE_DISABLED") {
				let snoozedTime = this.form.snooze;
				if (typeof snoozedTime === "number") {
					snoozedTime = Date.now() + snoozedTime;
				}
				snoozedTime = new Date(snoozedTime).toJSON();
				Object.assign(payload, { snoozedTime });
			}

			try {
				await this.$store.dispatch(
					"tickets/deviceStatus/changeStatus",
					payload
				);
			} catch (e) {
				this.error = e;
				throw e;
			} finally {
				this.loading = false;
			}

			this.$emit("reload");
			this.close();
		},
	},
};
</script>

<style lang="scss" scoped>
</style>
